import React, { useCallback, useMemo } from 'react';

import { Flex, Image, Row, Typography } from 'antd';

import DragAndDrop from '@/components/DragAndDrop';
import Dropdown from '@/components/forms/Dropdown';
import TextInput from '@/components/forms/TextInput';

interface ICarrierOption {
  carrierCode: string;
  label: string;
}

interface IDropdownOption {
  value: string;
  id: string;
}

interface IDuplicateShipmentCarrierFormProps {
  carrier: string;
  setCarrier: (id: string) => void;
  file: File | null;
  setFile: (file: File | null) => void;
  weightInGrams: string;
  onWeightChange: (value: string) => void;
  labelUrl?: string | null;
  carrierOptions: ICarrierOption[];
  dropdownDisabled?: boolean;
}

const DuplicateShipmentCarrierForm = ({
  carrier,
  setCarrier,
  file,
  setFile,
  weightInGrams,
  onWeightChange,
  labelUrl,
  carrierOptions,
  dropdownDisabled = false,
}: IDuplicateShipmentCarrierFormProps) => {
  const dropdownOptions = useMemo(
    () =>
      carrierOptions.map((c) => ({
        value: c.label,
        id: c.carrierCode,
      })),
    [carrierOptions]
  );

  const onCarrierDropDownChange = useCallback(
    (option: IDropdownOption) => {
      setCarrier(option.id);
    },
    [setCarrier]
  );

  const handleDropImage = useCallback(
    async (droppedFiles: File[]) => {
      if (droppedFiles[0]) setFile(droppedFiles[0]);
    },
    [setFile]
  );

  const handleChangeWeight = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onWeightChange(e.target.value);
    },
    [onWeightChange]
  );

  return (
    <>
      <Typography.Title level={5} style={{ paddingBottom: '1rem' }}>
        You can add a label (OPTIONAL):
      </Typography.Title>
      <Row justify="center" style={{ marginBottom: 20 }}>
        <Dropdown
          isDisabled={dropdownDisabled}
          options={dropdownOptions}
          getOptionValue={(option: IDropdownOption) => option.id}
          getOptionLabel={(option: IDropdownOption) => option.value}
          value={dropdownOptions.find((option) => option.id === carrier)}
          onChange={onCarrierDropDownChange}
        />
      </Row>
      <Row justify="center">
        <DragAndDrop
          onDrop={handleDropImage}
          text="Drop a PNG file to upload as the E-Label of this shipment"
          multiple={false}
          accept={{ 'image/png': [] }}
          height="7rem"
          fileDropped={!!file}
          removeFiles={() => setFile(null)}
        />
      </Row>
      {labelUrl && (
        <Flex align="center" gap="0.5rem" style={{ marginTop: 12 }}>
          <Image
            src={labelUrl}
            width={80}
            style={{ border: '1px solid #d9d9d9', borderRadius: 4 }}
            alt="Label preview"
            preview={{ mask: 'View label' }}
          />
          <Typography.Text type="secondary">Current label</Typography.Text>
        </Flex>
      )}
      <Row justify="center" style={{ marginTop: 15 }}>
        <TextInput
          label="Package weight (kg) :"
          name="weightInGrams"
          onChange={handleChangeWeight}
          value={weightInGrams}
        />
      </Row>
    </>
  );
};

export { DuplicateShipmentCarrierForm };
